import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import elementSettings from 'src/js/modules/element-settings';
import useTemporaryInlineStyles from 'src/js/modules/breakpoint-editing/hooks/use-temporary-inline-styles';
import useAutoPaddingSync from '../hooks/use-auto-padding-sync';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';

const { Paragraph, Icon } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { SliderControl },
  constants: { targetClasses, breakpointKeys },
  helpers: { createBatch, combineBatches, getResolvedStateWithAutoFill },
} = elementSettings;

const isMobile = bowser.mobile || bowser.tablet;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;
const RESTRICTED_VALUE = 32;

const TARGET_TO_TEMPORARY_STYLES = {
  [targetClasses.COL_PADDING_LEFT]: (value) => ({
    'padding-left': `clamp(0px, ${value}, calc(var(--ws-column-width, 100%) / 2))`,
  }),
  [targetClasses.COL_PADDING_RIGHT]: (value) => ({
    'padding-right': `clamp(0px, ${value}, calc(var(--ws-column-width, 100%) / 2))`,
  }),
};

const SPACINGS = [
  {
    id: 'left-spacing',
    target: targetClasses.COL_PADDING_LEFT,
    iconGlyph: 'padding-left',
  },
  {
    id: 'right-spacing',
    target: targetClasses.COL_PADDING_RIGHT,
    iconGlyph: 'padding-right',
  },
];

const parseValueWithUnit = (value) => `${parseFloat(value)}px`;

const getLiveElements = (elementIds) =>
  elementIds.map((elementId) => view.accessors.getLiveElement(elementId)).filter(Boolean);

const getValuesByBreakpoint = (value, isBreakpointEditingEnabled, activeBreakpointKey) => {
  if (isBreakpointEditingEnabled) {
    return { [activeBreakpointKey]: value };
  }

  return {
    [breakpointKeys.DEFAULT]: value,
    [breakpointKeys.MAX_XL]:
      parseFloat(value) > RESTRICTED_VALUE ? parseValueWithUnit(RESTRICTED_VALUE) : undefined,
  };
};

const getPopoverProps = ({ imgSrc, title, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getValuePopoverProps = (restrictedValue) => ({
  renderContent: () => {
    const desktopMessage = i18next.t(
      'Note: Padding over {value} will be scaled down on tablets and mobile devices.',
      { value: parseValueWithUnit(restrictedValue) }
    );
    const mobileMessage = i18next.t('Note: Padding over {value} will only be shown on desktop.', {
      value: parseValueWithUnit(restrictedValue),
    });

    return (
      <Paragraph appearance="white" size="small">
        {isMobile ? mobileMessage : desktopMessage}
      </Paragraph>
    );
  },
  checkVisibility: (value) => value > restrictedValue,
});

function HorizontalSpacingControl({ elementIds, targets, onChange }) {
  const { state, deviceMode } = useContext(ElementSettingsContext);
  const { getAutoPaddingBatch } = useAutoPaddingSync();
  const isBreakpointEditingEnabled = Boolean(deviceMode?.active);

  const { setTemporaryStyles, restoreTemporaryStyles } = useTemporaryInlineStyles();

  const resolvedState = useMemo(
    () => getResolvedStateWithAutoFill(state, deviceMode.breakpointKey),
    [deviceMode.breakpointKey, state]
  );

  const valuePopover = useMemo(
    () => (isBreakpointEditingEnabled ? null : getValuePopoverProps(RESTRICTED_VALUE)),
    [isBreakpointEditingEnabled]
  );

  const items = useMemo(
    () =>
      SPACINGS.map(({ id, target, iconGlyph }) => ({
        id,
        targets: [target],
        iconGlyph,
        ...(valuePopover && { valuePopover }),
      })),
    [valuePopover]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, targets, value, skipTracking }) => {
      const parsedValue = parseValueWithUnit(value);

      setTemporaryStyles({
        elements: getLiveElements(elementIds),
        styles: Object.assign(
          {},
          ...targets.map((target) => TARGET_TO_TEMPORARY_STYLES[target](parsedValue))
        ),
      });

      onChange({
        controlId,
        elementIds,
        targets,
        value: parsedValue,
        skipTracking,
        skipApplyChanges: true,
        shouldScrollToTarget: false,
      });
    },
    [onChange, setTemporaryStyles]
  );

  const handleAfterChange = useCallback(
    ({ controlId, elementIds, targets, value }) => {
      restoreTemporaryStyles({ elements: getLiveElements(elementIds) });

      const breakpointBasedValue = getValuesByBreakpoint(
        parseValueWithUnit(value),
        isBreakpointEditingEnabled,
        deviceMode.breakpointKey
      );
      const breakpointBasedBatch = createBatch({
        elementIds,
        targets,
        value: breakpointBasedValue,
      });

      onChange({
        controlId,
        elementIds,
        targets,
        value: breakpointBasedValue,
        batch: combineBatches([
          breakpointBasedBatch,
          getAutoPaddingBatch(breakpointBasedBatch, { elementIds }),
        ]),
      });
    },
    [deviceMode, getAutoPaddingBatch, isBreakpointEditingEnabled, onChange, restoreTemporaryStyles]
  );

  return (
    <SliderControl
      id={controlIds.HORIZONTAL_SPACING}
      elementIds={elementIds}
      resolvedState={resolvedState}
      targets={targets}
      title={i18next.t('Left&Right')}
      iconGlyph="padding-horizontal"
      mixedLabel={i18next.t('Mixed')}
      step={SLIDER_STEP}
      min={SLIDER_MIN}
      max={SLIDER_MAX}
      items={items}
      popover={getPopoverProps({
        imgSrc: previewGifs.COLUMN_HORIZONTAL_PADDING_GIF,
        title: i18next.t('Adjust the left and right spaces inside columns'),
        iconGlyph: 'padding-horizontal',
      })}
      {...(valuePopover && { valuePopover })}
      computeFallbackValue={computeSpacingFallback}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
    />
  );
}

HorizontalSpacingControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default HorizontalSpacingControl;
